import { ErrorMessage, useFormik } from "formik";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector, useStore } from "react-redux";
import { expenseActions } from "../../sagas/expenseSlice";
import * as Yup from 'yup';
import { Badge, Button, Card, CardBody, Col, Container, Form, FormFeedback, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Progress, Row, Spinner, Table } from "reactstrap";
import { FaCalendar, FaChevronLeft, FaChevronRight, FaCreditCard, FaCubes, FaDollarSign, FaEdit, FaFileExcel, FaFilePdf, FaFilter, FaPlus, FaSearch, FaTrash, FaUserFriends } from "react-icons/fa";
import Swal from "sweetalert2";
import { formatPrice } from "../../helpers/utility";
import Select from "react-select";
import { debounce } from "lodash";
import { commercialActions } from "../../sagas/commercialSlice";
import { commissionPlanActions } from "../../sagas/commissionPlanSlice";
import { deliveryActions } from "../../sagas/deliveryNoteSlice";
import { reglementActions } from "../../sagas/reglementSlice";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const PERIOD_OPTIONS = [
  { value: 'DAYS_15', label: '15 jours' },
  { value: 'DAYS_30', label: '30 jours' },
  { value: 'DAYS_45', label: '45 jours' },
  { value: 'DAYS_60', label: '60 jours' },
  { value: 'DAYS_90', label: '90 jours' },
];

const PERIOD_LABELS = {
  DAYS_15: "15 jours",
  DAYS_30: "30 jours",
  DAYS_45: "45 jours",
  DAYS_60: "60 jours",
  DAYS_90: "90 jours",
};

const getMonthRange = (monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const now = new Date();
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth();
  const endDate = isCurrentMonth ? now : lastDay;

  return {
    dateFrom: firstDay.toISOString().slice(0, 10),
    dateTo: endDate.toISOString().slice(0, 10),
  };
};

const getMonthLabel = (monthDate) =>
  new Intl.DateTimeFormat("fr-FR", { month: "long", year: "numeric" }).format(monthDate);

const CommercialList = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const { list, loading, performanceById, perfLoading, perfPeriod, perfRefDate } = useSelector((state) => state.commercials); // Adjust state path as needed
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { t } = useTranslation();
  const [filters, setFilters] = useState({
    query: ''
  });
  const [exporting, setExporting] = useState({ commercialId: null, type: null });
  const [selectedMonth, setSelectedMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));

  const [perfParams, setPerfParams] = useState(() => getMonthRange(new Date()));
  const [baseRatePct, setBaseRatePct] = useState(1);
  const baseRateDecimal = Number(baseRatePct || 0) / 100;


  const searchInputRef = useRef(null);

  useEffect(() => {
    dispatch(commercialActions.list({ ...filters })); // Fetch categories on component mount
    dispatch(commercialActions.loadPerformance({
      startDate: perfParams.dateFrom,
      endDate: perfParams.dateTo,
      baseRate: baseRateDecimal,
    }));
  }, []);

  useEffect(() => {
    dispatch(commercialActions.loadPerformance({
      startDate: perfParams.dateFrom,
      endDate: perfParams.dateTo,
      baseRate: baseRateDecimal,
    }));
  }, [perfParams.dateFrom, perfParams.dateTo, baseRatePct]);

  const shiftMonth = (delta) => {
    setSelectedMonth((previous) => {
      const nextMonth = new Date(previous.getFullYear(), previous.getMonth() + delta, 1);
      setPerfParams(getMonthRange(nextMonth));
      return nextMonth;
    });
  };

  useEffect(() => {
    searchInputRef.current?.focus();

    const handleKeyDown = (e) => {
      if (e.key === 'F2') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [list]);

  const clearFilters = () => {
    setFilters({
      query: '',
    });
  };

  const debouncedSearch = useCallback(
    debounce((query) => {
      dispatch(commercialActions.list({
        query: query
      }));
    }, 1500), // Wait 1.5 seconds after user stops typing
    [filters.query]
  );

  const handleCloseModal = () => {
    setIsModalVisible(false);
    formik.resetForm();
    dispatch(commercialActions.list({ ...filters })); // Fetch categories on component mount
  };

  const toggleModal = () => {
    setIsModalVisible(!isModalVisible);
  };


  const formik = useFormik({
    initialValues: {
      id: null,
      fullName: '',
      email: '',
      phone: '',
      contractType: '',
      hireDate: null,
      notes: '',
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      let payload = {
        data: {
          id: values.id,
          fullName: values.fullName,
          email: values.email,
          phone: values.phone,
          contractType: values.contractType,
          hireDate: values.hireDate,
          notes: values.notes,
        },
        onSuccess: () => {
          Swal.fire({
            icon: 'success',
            title: 'Succès',
            text: 'Commercial enregistrés avec succès!',
            confirmButtonText: 'OK',
          });
          handleCloseModal();
        }
      }
      if (values.id) {
        dispatch(commercialActions.update(payload));
      } else {
        dispatch(commercialActions.create(payload));
      }
    },
  });


  const handleAddCommercial = () => {
    formik.handleSubmit();
  }

  const handleDeleteCommercial = (id) => {
    Swal.fire({
      title: t('commercial.confirmDeleteTitle'),
      text: t('commercial.confirmDeleteText'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('common.yes'),
      cancelButtonText: t('common.no')
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(commercialActions.delete(
          { id }
        ));
      }
    });
  };

  const handleEditCommercial = (id) => {
    const commercial = list.find(com => com.id === id);
    if (commercial) {
      formik.setValues(commercial);
      toggleModal();
    }
  };

  const exportToExcel = (rows, sheetName, filename) => {
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, filename);
  };

  const handleExportBLs = async (commercial) => {
    setExporting({ commercialId: commercial.id, type: "bl" });
    try {
      const rows = await new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new Error("Timeout export BL"));
        }, 15000);

        dispatch(deliveryActions.list({
          commercialId: commercial.id,
          dateFrom: perfParams.dateFrom,
          dateTo: perfParams.dateTo,
          page: 0,
          size: 10000,
          onSuccess: () => {
            clearTimeout(timeoutId);
            const fetchedRows = store.getState()?.deliveryNotes?.list || [];
            resolve(fetchedRows);
          },
        }));
      });

      if (!rows.length) {
        Swal.fire({ icon: "info", title: "Aucun BL", text: "Aucun bon de livraison à exporter." });
        return;
      }

      const exportRows = rows.map((bl) => ({
        Numero: bl.numero,
        Date: bl.dateLivraison,
        Client: bl.clientNom,
        Commercial: bl.commercialNom || commercial.fullName,
        MontantHT: bl.montantTotalHT,
        MontantTVA: bl.montantTotalTaxe,
        MontantTTC: bl.montantTotalTTC,
        MontantPaye: bl.montantPaye,
        Statut: bl.statut || bl.status,
      }));

      exportToExcel(
        exportRows,
        "BLs",
        `BLs_${(commercial.fullName || commercial.code || commercial.id).replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.xlsx`
      );
    } catch (error) {
      Swal.fire({ icon: "error", title: "Erreur", text: "Export BLs impossible." });
    } finally {
      setExporting({ commercialId: null, type: null });
    }
  };

  const handleExportPaiements = async (commercial) => {
    setExporting({ commercialId: commercial.id, type: "payments" });
    try {
      const rows = await new Promise((resolve, reject) => {
        let hasStartedLoading = false;
        const timeoutId = setTimeout(() => {
          unsubscribe?.();
          reject(new Error("Timeout export paiements"));
        }, 15000);

        const unsubscribe = store.subscribe(() => {
          const reglementState = store.getState()?.reglements;
          if (reglementState?.loading === true) {
            hasStartedLoading = true;
          }
          if (reglementState && hasStartedLoading && reglementState.loading === false) {
            clearTimeout(timeoutId);
            unsubscribe();
            resolve(reglementState.list || []);
          }
        });

        dispatch(reglementActions.searchRequest({
          query: commercial?.fullName || "",
          dateFrom: perfParams.dateFrom,
          dateTo: perfParams.dateTo,
          page: 0,
          size: 10000,
        }));
      });

      if (!rows.length) {
        Swal.fire({ icon: "info", title: "Aucun paiement", text: "Aucun paiement à exporter." });
        return;
      }

      const exportRows = rows.map((r) => ({
        Reference: r.reference || r.id,
        Date: r.dateReglement,
        Client: r.nomClient,
        Commercial: r.commercialNom || commercial.fullName,
        Mode: r.modeReglement,
        MontantRegle: r.montantRegle,
        MontantAffecte: r.montantAffecte,
        Statut: r.statut,
        Type: r.typeReglement,
        Factures: r.numFactures,
        BonLivraisons: r.numBonLivraisons,
      }));

      exportToExcel(
        exportRows,
        "Paiements",
        `Paiements_${(commercial.fullName || commercial.code || commercial.id).replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.xlsx`
      );
    } catch (error) {
      Swal.fire({ icon: "error", title: "Erreur", text: "Export paiements impossible." });
    } finally {
      setExporting({ commercialId: null, type: null });
    }
  };

  const getSummaryRows = () =>
    list.map((ex) => {
      const perf = performanceById?.[ex.id];
      const baseTotal = Number(perf?.baseTotal || 0);
      const estimatedCommission = (baseTotal * baseRatePct) / 100;
      return {
        Code: ex.code || "-",
        Commercial: ex.fullName || "-",
        Realise: baseTotal,
        CommissionEstimee: estimatedCommission,
      };
    });

  const handleExportSummaryExcel = () => {
    const rows = getSummaryRows();
    if (!rows.length) {
      Swal.fire({ icon: "info", title: "Aucune donnée", text: "Aucun commercial à exporter." });
      return;
    }
    exportToExcel(rows, "Commerciaux", `Commerciaux_${perfParams.dateFrom}_${perfParams.dateTo}.xlsx`);
  };

  const handleExportSummaryPdf = () => {
    const rows = getSummaryRows();
    if (!rows.length) {
      Swal.fire({ icon: "info", title: "Aucune donnée", text: "Aucun commercial à exporter." });
      return;
    }

    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(12);
    doc.text(`Liste commerciaux - ${getMonthLabel(selectedMonth)}`, 14, 14);
    doc.setFontSize(10);
    doc.text(`Periode: ${perfParams.dateFrom} au ${perfParams.dateTo}`, 14, 20);

    autoTable(doc, {
      startY: 26,
      head: [["Code", "Commercial", "Realise", "Commission estimee"]],
      body: rows.map((r) => [
        r.Code,
        r.Commercial,
        formatPrice(r.Realise),
        formatPrice(r.CommissionEstimee),
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [52, 58, 64] },
    });

    doc.save(`Commerciaux_${perfParams.dateFrom}_${perfParams.dateTo}.pdf`);
  };

  return (
    <Container fluid>
      {loading ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
          <div className="mt-2 text-muted">{t('common.loading')}</div>
        </div>
      ) : (
        <>
          <Row className="mb-4" >
            <Col>
              <Card className="shadow-sm">
                <CardBody className="d-flex justify-content-between align-items-center">
                  <h4 className="mb-0 d-flex align-items-center">
                    <FaCubes className="text-primary me-2" />
                    {t('commercial.list')}
                  </h4>
                  <Button
                    color="primary"
                    onClick={() => toggleModal()} // Go back to the previous page
                    className="d-flex align-items-center"
                  >
                    <FaPlus className="me-2" />
                    {t('commercial.new')}
                  </Button>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Card className="shadow-sm">
            <CardBody>
              <div className="filter-container mb-4">
                <Row className="mb-3 g-2 align-items-end">
                  <Col lg={5} md={5} sm={12} xs={12}>
                    <FormGroup className="mb-0">
                      <Label className="mb-1">{t('reglement.search')}</Label>
                      <div className="input-group">
                        <span className="input-group-text bg-light">
                          <FaSearch />
                        </span>
                        <Input
                          type="text"
                          innerRef={searchInputRef}
                          placeholder={t('commercial.searchPlaceholder')}
                          value={filters.query}
                          onChange={e => {
                            const value = e.target.value.toUpperCase();
                            setFilters(prevFilters => ({
                              ...prevFilters,
                              query: value
                            }));
                            debouncedSearch(value);
                          }}
                          className="text-uppercase"
                          style={{ textTransform: 'uppercase' }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              debouncedSearch(e.target.value.toUpperCase());
                            }
                          }}
                        />
                      </div>
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup className="mb-0">
                      <Label className="mb-1">Mois</Label>
                      <div className="d-flex align-items-center gap-2">
                        <Button color="light" onClick={() => shiftMonth(-1)} title="Mois précédent">
                          <FaChevronLeft />
                        </Button>
                        <div className="flex-grow-1 text-center border rounded px-2 py-2 text-capitalize">
                          {getMonthLabel(selectedMonth)}
                        </div>
                        <Button color="light" onClick={() => shiftMonth(1)} title="Mois suivant">
                          <FaChevronRight />
                        </Button>
                      </div>
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup className="mb-0">
                      <Label className="mb-1">Taux base (%)</Label>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        value={baseRatePct}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          setBaseRatePct(Number.isFinite(value) && value >= 0 ? value : 0);
                        }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={1} className="d-flex align-items-end">
                    {perfLoading && <Spinner size="sm" className="ms-2" />}
                  </Col>
                </Row>
                <Row className="g-2 mb-2">
                  <Col className="d-flex justify-content-end">
                    <Button color="success" className="me-2" onClick={handleExportSummaryExcel}>
                      <FaFileExcel className="me-1" />
                      Export Excel
                    </Button>
                    <Button color="danger" onClick={handleExportSummaryPdf}>
                      <FaFilePdf className="me-1" />
                      Export PDF
                    </Button>
                  </Col>
                </Row>
              </div>
              <Table bordered>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Nom complet</th>
                    <th>Réalisé (Total Encaissé)</th>     {/** new */}
                    <th>Est. Commission</th>    {/** new */}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.length > 0 && list.map((ex) => {
                    const perf = performanceById[ex.id]; // { plan, baseUsed, baseTotal, objective, totalCommission, ... }
                    const baseTotal = Number(perf?.baseTotal || 0);
                    const objective = Number(perf?.objective || 0);
                    const pct = objective > 0 ? Math.min(100, Math.round((baseTotal / objective) * 100)) : 0;
                    const estimatedCommission = (baseTotal * baseRatePct) / 100;


                    return (
                      <tr key={ex.id}>
                        <td>{ex.code}</td>
                        <td>{ex.fullName}</td>
                        {/* Réalisé */}
                        <td>{formatPrice(baseTotal)}</td>
                        

                        {/* Est. Commission (preview total) */}
                        <td><strong>{formatPrice(estimatedCommission)}</strong></td>

                        <td>
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => handleDeleteCommercial(ex.id)}
                          >
                            <FaTrash />
                          </Button>
                          <Button
                            className="ms-1"
                            color="warning"
                            size="sm"
                            onClick={() => handleEditCommercial(ex.id)}
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            className="ms-1"
                            color="success"
                            size="sm"
                            onClick={() => handleExportBLs(ex)}
                            disabled={exporting.commercialId === ex.id && exporting.type === "bl"}
                            title="Exporter BLs (Excel)"
                          >
                            {exporting.commercialId === ex.id && exporting.type === "bl" ? <Spinner size="sm" /> : "Ventes"}
                          </Button>
                          <Button
                            className="ms-1"
                            color="info"
                            size="sm"
                            onClick={() => handleExportPaiements(ex)}
                            disabled={exporting.commercialId === ex.id && exporting.type === "payments"}
                            title="Exporter paiements (Excel)"
                          >
                            {exporting.commercialId === ex.id && exporting.type === "payments" ? <Spinner size="sm" /> : "Paiements"}
                          </Button>
                          
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
              {list.length === 0 && (
                <div className="text-center mt-4">
                  <FaUserFriends size={50} className="text-muted" />
                  <h5 className="mt-2">{t('commercial.noCommercials')}</h5>
                </div>
              )}
            </CardBody>
          </Card>
        </>)}
      <Modal isOpen={isModalVisible} toggle={handleCloseModal} size="xl">
        <ModalHeader toggle={handleCloseModal}>
          {formik.values.id ? `${t('commercial.edit')} ${formik.values.code}` : t('commercial.new')}
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={formik.handleSubmit}>
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label for="fullName">{t('commercial.fullName')}</Label>
                  <Input
                    type="text"
                    name="fullName"
                    id="fullName"
                    value={formik.values.fullName}
                    onChange={formik.handleChange}
                    required
                    maxLength={120}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="email">{t('commercial.email')}</Label>
                  <Input
                    type="email"
                    name="email"
                    id="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    maxLength={120}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="phone">{t('commercial.phone')}</Label>
                  <Input
                    type="text"
                    name="phone"
                    id="phone"
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    maxLength={30}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>

              <Col md={4}>
                <FormGroup>
                  <Label for="contractType">{t('commercial.contractType')}</Label>
                  <Input
                    type="select"
                    name="contractType"
                    id="contractType"
                    value={formik.values.contractType}
                    onChange={formik.handleChange}
                    required
                  >
                    <option value="">{t('common.select')}</option>
                    <option value="CDD">{t('commercial.cdd')}</option>
                    <option value="CDI">{t('commercial.cdi')}</option>
                    <option value="FREELANCE">{t('commercial.freelance')}</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="hireDate">{t('commercial.hireDate')}</Label>
                  <Input
                    type="date"
                    name="hireDate"
                    id="hireDate"
                    value={formik.values.hireDate}
                    onChange={formik.handleChange}
                    required
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label for="notes">{t('commercial.notes')}</Label>
                  <Input
                    type="textarea"
                    name="notes"
                    id="notes"
                    value={formik.values.notes}
                    onChange={formik.handleChange}
                    maxLength={500}
                  />
                </FormGroup>
              </Col>
            </Row>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleAddCommercial}>
            {t('common.save')}
          </Button>{' '}
          <Button color="secondary" onClick={handleCloseModal}>
            {t('common.cancel')}
          </Button>
        </ModalFooter>
      </Modal>


    </Container>
  );
};

export default CommercialList;
